import Lucide from "src/base-components/Lucide";
import MessageReactionPicker from "src/components/MessageReactionPicker";
import MessageTicks from "./MessageStatus";
import { ChatMessage, ReplyTo, useOneToOneChat } from "src/pubnub";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { usePubNub } from "pubnub-react";
import TestImage from "../../../assets/images/TestImages/user.png";
import ScrollToBottomButton from "./ScrollToBottomButton";
import ActionModal from "./ActionModal";
import ChatMessageInput from "./ChatMessageInput";
import MessageActionMenu from "./MessageActionMenu";
import FilePreview from "./FilePreview";
import LazyFileAttachment from "./LazyFileAttachment";
import { useAppDispatch } from "src/stores";
import { setAlert } from "src/stores/basicSlices";

/* ------------------ ONE-TO-ONE CHAT COMPONENT ------------------ */

interface OneToOneChatPanelProps {
    currentRoomId: string;
    currentUserId: string;
    currentUserName: string;
    targetUserId: string;
    targetUserName: string;
    channelId?: string; // override the generated channel (admin viewing ongoing chat)
    onBack?: () => void;
    readOnly?: boolean; // true when viewing from Ongoing Chats tab (view-only mode)
}

// Allowed file types for attachments
// VIDEO UPLOAD DISABLED: removed video/* from allowed types
const ALLOWED_FILE_TYPES = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt";
const MAX_FILE_SIZE_MB = 4; // Max 4MB per file
const MAX_FILES_PER_MESSAGE = 10; // Max 10 files per message

/** Shape for each selected file with its preview URL */
interface SelectedFileItem {
    file: File;
    preview: string | null; // base64 for images, null for docs (video upload disabled)
    id: string; // unique identifier for React keys
}

const OneToOneChatPanel: React.FC<OneToOneChatPanelProps> = ({
    currentRoomId,
    currentUserId,
    currentUserName,
    targetUserId,
    targetUserName,
    channelId: channelIdProp,
    onBack,
    readOnly = false,
}) => {
    const pubnub = usePubNub(); // Get PubNub instance for file downloads
    const dispatch = useAppDispatch();

    const [inputMessage, setInputMessage] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<SelectedFileItem[]>([]);
    const [replyingTo, setReplyingTo] = useState<ReplyTo | null>(null);
    const [isSending, setIsSending] = useState(false);

    // Edit state
    const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState<{
        isOpen: boolean;
        message: ChatMessage | null;
    }>({ isOpen: false, message: null });

    // Message action menu state
    const [actionMenuState, setActionMenuState] = useState<{
        isOpen: boolean;
        message: ChatMessage | null;
        position: { x: number; y: number };
    }>({ isOpen: false, message: null, position: { x: 0, y: 0 } });

    // Reaction picker state
    const [reactionPickerState, setReactionPickerState] = useState<{
        isOpen: boolean;
        messageTimetoken: string | null;
        position: { x: number; y: number };
    }>({ isOpen: false, messageTimetoken: null, position: { x: 0, y: 0 } });

    // Expanded messages state
    const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});

    const toggleExpandMessage = (msgId: string) => {
        setExpandedMessages((prev) => ({ ...prev, [msgId]: !prev[msgId] }));
    };

    // Scroll-to-reply highlight state
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    const [isScrollingToReply, setIsScrollingToReply] = useState(false);

    // Scroll-to-bottom button
    const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
    const [userHasScrolledUp, setUserHasScrolledUp] = useState<boolean>(false);

    // Floating date label (WhatsApp style: show on scroll, fade after idle)
    const [floatingDateLabel, setFloatingDateLabel] = useState("");
    const [isFloatingDateVisible, setIsFloatingDateVisible] = useState(false);
    const scrollHideTimerRef = useRef<ReturnType<typeof setTimeout>>();

    // Cleanup floating date timer on unmount
    useEffect(() => {
        return () => {
            if (scrollHideTimerRef.current) clearTimeout(scrollHideTimerRef.current);
        };
    }, []);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const prevScrollHeightRef = useRef<number>(0);
    const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const {
        messages,
        isLoading,
        isLoadingMore,
        hasMore,
        error,
        isConnected,
        isTargetOnline,
        isTargetTyping,
        reactions,
        deletedForMe,
        sendMessage,
        editMessage,
        deleteMessage,
        sendTypingIndicator,
        addReaction,
        removeReaction,
        markAsRead,
        getMessageStatus,
        loadMoreMessages,
        loadMessagesToTimetoken,
        clearMessages
    } = useOneToOneChat({
        currentRoomId,
        currentUserId,
        targetUserId,
        currentUserName,
        channelId: channelIdProp,
        autoSubscribe: true,
    });

    // Auto-resize textarea
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, [inputMessage]);

    // Scroll to bottom on new messages
    useEffect(() => {
        if (messages.length > 0 && !isLoading && !userHasScrolledUp) {
            const lastMessage = messages[0];
            const isOwnMessage = lastMessage?.senderId === currentUserId;
            const isNewMessage = lastMessage?.timestamp && (Date.now() - lastMessage.timestamp) < 5000;

            const shouldAutoScroll = isInitialLoadRef.current || isOwnMessage || isNewMessage;

            if (shouldAutoScroll) {
                requestAnimationFrame(() => {
                    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
                });
                isInitialLoadRef.current = false;
            }
        }
    }, [messages, isLoading, userHasScrolledUp, currentUserId]);

    const isInitialLoadRef = useRef(true);

    useEffect(() => {
        isInitialLoadRef.current = true;
        setUserHasScrolledUp(false);
        prevScrollHeightRef.current = 0;

        const timeoutId = setTimeout(() => {
            if (messages.length > 0) {
                messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
                isInitialLoadRef.current = false;
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [targetUserId]);

    // Note: clearMessages is now handled inside useOneToOneChat's subscribe effect
    // when targetUserId/channelId changes, so no separate clearMessages call needed.

    useEffect(() => {
        if (!isLoadingMore && messagesContainerRef.current && prevScrollHeightRef.current > 0) {
            const container = messagesContainerRef.current;
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - prevScrollHeightRef.current;
            if (scrollDiff > 0) {
                container.scrollTop = scrollDiff;
            }
            prevScrollHeightRef.current = 0;
        }
    }, [isLoadingMore, messages]);

    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        setShowScrollToBottom(distanceFromBottom > 100);
        setUserHasScrolledUp(distanceFromBottom > 50);

        // Floating date label — find the last date separator scrolled past the container top
        const separators = container.querySelectorAll<HTMLElement>("[data-date-label]");
        const containerTop = container.getBoundingClientRect().top;
        let currentLabel = "";
        separators.forEach((el) => {
            if (el.getBoundingClientRect().top <= containerTop + 20) {
                currentLabel = el.getAttribute("data-date-label") || "";
            }
        });
        // If no separator scrolled past, use the first visible one
        if (!currentLabel && separators.length > 0) {
            currentLabel = separators[0].getAttribute("data-date-label") || "";
        }
        if (currentLabel) {
            setFloatingDateLabel(currentLabel);
            setIsFloatingDateVisible(true);
        }
        // Auto-hide after 1.5s of no scrolling
        if (scrollHideTimerRef.current) clearTimeout(scrollHideTimerRef.current);
        scrollHideTimerRef.current = setTimeout(() => setIsFloatingDateVisible(false), 1500);

        if (container.scrollTop < 100 && hasMore && !isLoadingMore && !isLoading) {
            prevScrollHeightRef.current = container.scrollHeight;
            loadMoreMessages();
        }
    }, [hasMore, isLoadingMore, isLoading, loadMoreMessages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Mark only the latest incoming message as read (no need for all — indicates "read up to here")
    const lastReadTimetokenRef = useRef<string | null>(null);
    useEffect(() => {
        if (readOnly) return;
        for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg.senderId !== currentUserId && msg.timetoken) {
                const tt = String(msg.timetoken);
                if (tt !== lastReadTimetokenRef.current) {
                    lastReadTimetokenRef.current = tt;
                    markAsRead(tt);
                }
                break;
            }
        }
    }, [messages, currentUserId, markAsRead, readOnly]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputMessage(e.target.value);
        sendTypingIndicator(true);
    };

    const formatTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const isSameDay = (d1: Date, d2: Date) =>
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();

    const getDateLabel = (timestamp: number): string => {
        const date = new Date(timestamp);
        const today = new Date();
        if (isSameDay(date, today)) return "Today";

        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        if (isSameDay(date, yesterday)) return "Yesterday";

        return date.toLocaleDateString(undefined,
            date.getFullYear() === today.getFullYear()
                ? { day: "2-digit", month: "short" }
                : { day: "2-digit", month: "short", year: "numeric" }
        );
    };

    /** Handle multi-file selection from input */
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;

        const incoming = Array.from(fileList);
        const totalAfterAdd = selectedFiles.length + incoming.length;

        if (totalAfterAdd > MAX_FILES_PER_MESSAGE) {
            dispatch(setAlert({
                text: `You can attach up to ${MAX_FILES_PER_MESSAGE} files per message.`, alertType: "Swal",
                swalIcon: "error"
            }));
            e.target.value = "";
            return;
        }

        // Validate each file size
        const oversized = incoming.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
        if (oversized.length > 0) {
            dispatch(setAlert({
                text: `File size must be less than ${MAX_FILE_SIZE_MB}MB. Skipped: ${oversized.map((f) => f.name).join(", ")}`, alertType: "Swal",
                swalIcon: "error"
            }));
        }

        const validFiles = incoming.filter((f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024);
        if (validFiles.length === 0) {
            e.target.value = "";
            return;
        }

        // VIDEO UPLOAD DISABLED: reject any video files
        const nonVideoFiles = validFiles.filter((f) => !f.type.startsWith("video/"));
        if (nonVideoFiles.length < validFiles.length) {
            dispatch(setAlert({
                text: "Video uploads are not supported. Please upload a different file type.",
                alertType: "Swal",
                swalIcon: "error",
            }));
        }
        if (nonVideoFiles.length === 0) {
            e.target.value = "";
            return;
        }

        // Build SelectedFileItem[] with previews
        const newItems: SelectedFileItem[] = nonVideoFiles.map((file) => {
            const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            return { file, preview: null, id };
        });

        // Generate previews asynchronously for images
        // VIDEO UPLOAD DISABLED: removed video preview generation
        newItems.forEach((item) => {
            if (item.file.type.startsWith("image/") /* || item.file.type.startsWith("video/") */) {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    setSelectedFiles((prev) =>
                        prev.map((f) => (f.id === item.id ? { ...f, preview: ev.target?.result as string } : f))
                    );
                };
                reader.readAsDataURL(item.file);
            }
        });

        setSelectedFiles((prev) => [...prev, ...newItems]);

        // Reset input so same files can be re-selected
        e.target.value = "";
    };

    /** Remove a single file by id */
    const handleRemoveFile = (fileId?: string) => {
        if (fileId) {
            setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
        } else {
            // Clear all
            setSelectedFiles([]);
        }
    };

    const getFileIcon = (fileType: string) => {
        if (fileType?.startsWith("image/")) return "Image";
        // VIDEO UPLOAD DISABLED: video icon mapping commented out
        // if (fileType?.startsWith("video/")) return "Video";
        if (fileType?.includes("pdf")) return "FileText";
        if (fileType?.includes("word") || fileType?.includes("document")) return "FileText";
        if (fileType?.includes("excel") || fileType?.includes("sheet")) return "FileSpreadsheet";
        return "File";
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    };

    const handleSendWithFile = async () => {
        if (isSending) return;

        const hasText = inputMessage.trim().length > 0;
        const hasFiles = selectedFiles.length > 0;

        if (!hasText && !hasFiles) return;

        setIsSending(true);

        try {
            const messageText = inputMessage.trim();
            // Pass raw File objects to the hook (it already supports File[] | FileWithPreview[])
            const rawFiles = hasFiles ? selectedFiles.map((f) => f.file) : undefined;

            await sendMessage(messageText, rawFiles, replyingTo || undefined);

            // Clear everything on success
            handleRemoveFile(); // clears all files
            setInputMessage("");
            setReplyingTo(null);
            sendTypingIndicator(false);

            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        } catch (error: any) {
            console.error("Failed to send message:", error);
        } finally {
            setIsSending(false);
        }
    };

    const handleReplyToMessage = (msg: ChatMessage) => {
        // Build reply text — include file context when message has attachments
        const msgFiles = msg.files?.length ? msg.files : msg.file ? [msg.file] : [];
        let replyText = msg.text || "";
        if (msgFiles.length > 0 && !replyText) {
            // No text, just files — summarize
            const imageCount = msgFiles.filter((f) => f.type?.startsWith("image/")).length;
            // VIDEO UPLOAD DISABLED: video count commented out
            // const videoCount = msgFiles.filter((f) => f.type?.startsWith("video/")).length;
            const docCount = msgFiles.length - imageCount;
            const parts: string[] = [];
            if (imageCount) parts.push(`${imageCount} image${imageCount > 1 ? "s" : ""}`);
            // if (videoCount) parts.push(`${videoCount} video${videoCount > 1 ? "s" : ""}`);
            if (docCount) parts.push(`${docCount} file${docCount > 1 ? "s" : ""}`);
            replyText = `📎 ${parts.join(", ")}`;
        } else if (msgFiles.length > 0 && replyText) {
            replyText = `📎 ${replyText}`;
        }
        if (replyText.length > 100) replyText = replyText.substring(0, 100) + "...";

        setReplyingTo({
            messageId: msg.id,
            text: replyText,
            senderId: msg.senderId,
            senderName: msg.senderName,
            timetoken: msg.timetoken,
        });
    };

    const cancelReply = () => {
        setReplyingTo(null);
    };

    const scrollToMessage = useCallback(
        async (messageId: string, timetoken?: string | number) => {
            const el = messageRefs.current.get(messageId);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                setHighlightedMessageId(messageId);
                setTimeout(() => setHighlightedMessageId(null), 2000);
                return;
            }

            let resolvedTimetoken = timetoken ? String(timetoken) : undefined;
            if (!resolvedTimetoken) {
                const match = messages.find((m) => m.id === messageId);
                if (match?.timetoken) {
                    resolvedTimetoken = String(match.timetoken);
                }
            }

            if (!resolvedTimetoken) return;

            setIsScrollingToReply(true);
            const found = await loadMessagesToTimetoken(resolvedTimetoken);
            setIsScrollingToReply(false);

            if (found) {
                await new Promise((r) => setTimeout(r, 300));
                const target = messageRefs.current.get(messageId);
                if (target) {
                    target.scrollIntoView({ behavior: "smooth", block: "center" });
                    setHighlightedMessageId(messageId);
                    setTimeout(() => setHighlightedMessageId(null), 2000);
                }
            }
        },
        [loadMessagesToTimetoken, messages],
    );

    const handleEmojiSelect = (emoji: string) => {
        setInputMessage((prev) => prev + emoji);
    };

    const handleShowActionMenu = (e: React.MouseEvent, msg: ChatMessage) => {
        e.preventDefault();
        e.stopPropagation();
        setActionMenuState({
            isOpen: true,
            message: msg,
            position: { x: e.clientX, y: e.clientY },
        });
    };

    const closeActionMenu = () => {
        setActionMenuState({ isOpen: false, message: null, position: { x: 0, y: 0 } });
    };

    const handleStartEdit = (msg: ChatMessage) => {
        setEditingMessage(msg);
        setInputMessage(msg.text);
        closeActionMenu();
    };

    const cancelEdit = () => {
        setEditingMessage(null);
        setInputMessage("");
    };

    const handleSaveEdit = async () => {
        if (!editingMessage || !inputMessage.trim()) return;
        await editMessage(editingMessage.id, inputMessage);
        setEditingMessage(null);
        setInputMessage("");
    };

    const handleShowDeleteModal = (msg: ChatMessage) => {
        setDeleteModal({ isOpen: true, message: msg });
        closeActionMenu();
    };

    const handleDelete = (deleteForEveryone: boolean) => {
        if (deleteModal.message) {
            deleteMessage(deleteModal.message.id, deleteForEveryone);
        }
        setDeleteModal({ isOpen: false, message: null });
    };

    const handleMessageContextMenu = (e: React.MouseEvent, messageTimetoken: string) => {
        e.preventDefault();
        setReactionPickerState({
            isOpen: true,
            messageTimetoken,
            position: { x: e.clientX, y: e.clientY },
        });
    };

    const handleAddReaction = async (emoji: string) => {
        if (!reactionPickerState.messageTimetoken) return;
        await addReaction(reactionPickerState.messageTimetoken, emoji);
    };

    const closeReactionPicker = () => {
        setReactionPickerState({ isOpen: false, messageTimetoken: null, position: { x: 0, y: 0 } });
    };

    return (
        <div className="flex flex-col flex-1 h-full min-w-0">
            {/* Header */}
            <div className="flex items-center gap-3 border-b p-4 bg-white">
                {/* <img src={TestImage} className="w-10 h-10 rounded-full" /> */}
                <div className="w-10 h-10 rounded-full bg-[linear-gradient(15deg,_rgb(76,29,149)_15%,_rgb(159,18,57)_90%)] opacity-75 flex items-center justify-center text-white font-bold text-lg">
                    {targetUserName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                        {targetUserName}
                        {isTargetOnline && (
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        )}
                    </div>
                    <div className="text-xs text-slate-500">
                        {isTargetTyping
                            ? "Typing..."
                            : isTargetOnline
                                ? "Online"
                                : ""}
                    </div>
                </div>
                {!isConnected && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        Connecting...
                    </span>
                )}
            </div>

            {/* Error Banner */}
            {error && (
                <div className="bg-red-50 text-red-600 text-sm px-4 py-2 border-b border-red-100">
                    {error}
                </div>
            )}

            {/* Messages */}
            <div className="relative flex-1 overflow-hidden">
                {/* Floating date pill — WhatsApp style: shows on scroll, fades after idle */}
                <div
                    className={`absolute top-2 left-1/2 -translate-x-1/2 z-30 pointer-events-none transition-opacity duration-300 ${isFloatingDateVisible && floatingDateLabel ? "opacity-100" : "opacity-0"
                        }`}
                >
                    <span className="text-[11px] text-slate-500 bg-white px-3 py-1 rounded-full shadow-md">
                        {floatingDateLabel}
                    </span>
                </div>

                {isScrollingToReply && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-sm">
                        <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-lg">
                            <Lucide icon="Loader2" className="w-5 h-5 animate-spin text-purple-600" />
                            <span className="text-sm text-gray-600">Loading messages…</span>
                        </div>
                    </div>
                )}
                <div
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                    className="h-full p-4 overflow-y-auto overflow-x-hidden space-y-3 bg-[#F7F1F5]"
                >
                    {isLoadingMore && (
                        <div className="flex justify-center py-2">
                            <Lucide icon="Loader2" className="w-5 h-5 animate-spin text-purple-600" />
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex justify-center items-center h-full">
                            <Lucide icon="Loader2" className="w-6 h-6 animate-spin text-purple-600" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-full text-slate-400">
                            <Lucide icon="MessageCircle" className="w-12 h-12 mb-2" />
                            <p>No messages yet. Start the conversation!</p>
                        </div>
                    ) : (
                        messages.map((msg: any, idx: number) => {
                            if (deletedForMe.has(msg.id)) return null;

                            const isMine = msg.senderId === currentUserId;
                            const messageTimetoken = msg.timetoken;
                            const msgReactions = messageTimetoken ? (reactions[messageTimetoken] || []) : [];
                            const canReact = !!messageTimetoken && !msg.isDeleted;

                            const isExpanded = !!expandedMessages[String(msg.id)];
                            const shouldTruncate = typeof msg.text === "string" && msg.text.length > 100;

                            const prevMsg = messages[idx - 1];
                            const msgDate = new Date(msg.timestamp ?? Date.now());
                            const prevDate = prevMsg ? new Date(prevMsg.timestamp ?? 0) : null;
                            const showDateSeparator = !prevDate || !isSameDay(msgDate, prevDate);

                            // Collect all files for this message
                            const msgFiles = msg.files?.length ? msg.files : msg.file ? [msg.file] : [];
                            const hasAnyFiles = msgFiles.length > 0;

                            return (
                                <React.Fragment key={msg.id}>
                                    {showDateSeparator && (
                                        <div data-date-label={getDateLabel(msgDate.getTime())} className={`flex justify-center my-3 transition-opacity duration-300 ${isFloatingDateVisible && floatingDateLabel === getDateLabel(msgDate.getTime()) ? "opacity-0" : "opacity-100"}`}>
                                            <span className="text-[11px] text-slate-500 bg-white px-3 py-1 rounded-full shadow-sm">
                                                {getDateLabel(msgDate.getTime())}
                                            </span>
                                        </div>
                                    )}

                                    <div
                                        data-message-id={msg.id}
                                        className={`flex ${isMine ? "justify-end" : "justify-start"} ${highlightedMessageId === msg.id ? "animate-highlight-msg" : ""}`}
                                        ref={(el) => {
                                            if (el) messageRefs.current.set(msg.id, el);
                                            else messageRefs.current.delete(msg.id);
                                        }}
                                    >
                                        <div className={`relative group max-w-[75%] ${isMine ? "order-1" : ""} ${msgReactions.length > 0 ? "mb-4" : ""}`}>
                                            {!msg.isDeleted && !readOnly && (
                                                <div className={`absolute top-1 opacity-0 group-hover:opacity-100 transition-all duration-200 flex gap-0.5 z-10 ${isMine ? "left-0 -translate-x-full pr-1" : "right-0 translate-x-full pl-1"}`}>
                                                    <button
                                                        onClick={() => handleReplyToMessage(msg)}
                                                        className="p-1.5 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
                                                        title="Reply"
                                                    >
                                                        <Lucide icon="Reply" className="w-3.5 h-3.5 text-[#54656f]" />
                                                    </button>
                                                    {isMine && Date.now() - msg.timestamp < 60_000 && (
                                                        <button
                                                            onClick={(e) => handleShowActionMenu(e, msg)}
                                                            className="p-1.5 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
                                                            title="More options"
                                                        >
                                                            <Lucide icon="ChevronDown" className="w-3.5 h-3.5 text-[#54656f]" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            <div
                                                className={`relative px-[9px] pt-[5px] pb-[7px] text-[13.5px] leading-[19px] shadow-sm overflow-hidden ${msg.isDeleted
                                                    ? "bg-white/80 italic text-[#8696a0] rounded-lg"
                                                    : isMine
                                                        ? "text-white rounded-tl-lg rounded-tr-lg rounded-bl-lg rounded-br-none"
                                                        : "bg-white text-[#111b21] rounded-tl-none rounded-tr-lg rounded-br-lg rounded-bl-lg"
                                                    }`}
                                                style={{
                                                    ...(isMine && !msg.isDeleted && { background: 'linear-gradient(15deg, #4C1D95 15%, #9F1239 90%)', opacity: '75%' })
                                                }}
                                                onContextMenu={(e) => {
                                                    if (canReact) handleMessageContextMenu(e, messageTimetoken);
                                                }}
                                                onDoubleClick={() => !msg.isDeleted && handleReplyToMessage(msg)}
                                            >
                                                {!msg.isDeleted && (
                                                    <div
                                                        className={`absolute top-0 w-3 h-3 ${isMine
                                                            ? "right-0 translate-x-1/2"
                                                            : "left-0 -translate-x-1/2 bg-white"
                                                            }`}
                                                        style={{
                                                            clipPath: isMine
                                                                ? "polygon(0 0, 100% 0, 0 100%)"
                                                                : "polygon(100% 0, 0 0, 100% 100%)",
                                                        }}
                                                    />
                                                )}

                                                {msg.isDeleted ? (
                                                    <div className="flex items-center gap-2 py-0.5">
                                                        <Lucide icon="Ban" className="w-4 h-4" />
                                                        <span className="text-[13px]">This message was deleted</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {readOnly && (
                                                            <div className={`text-[11px] font-semibold mb-0.5 ${isMine ? "text-white/90" : "text-[#6B21A8]"}`}>
                                                                {msg.senderName || (isMine ? currentUserName : targetUserName)}
                                                            </div>
                                                        )}
                                                        {msg.replyTo && (
                                                            <div
                                                                className={`mb-1.5 p-2 rounded-md border-l-4 text-[12px] cursor-pointer hover:opacity-90 transition-opacity ${isMine
                                                                    ? "bg-white/20 border-white/60"
                                                                    : "bg-[#f0f0f0] border-[#06cf9c]"
                                                                    }`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    scrollToMessage(msg.replyTo!.messageId, msg.replyTo!.timetoken);
                                                                }}
                                                            >
                                                                <div className={`font-semibold mb-0.5 ${isMine ? "text-white/90" : "text-[#06cf9c]"}`}>
                                                                    {msg.replyTo.senderId === currentUserId ? "You" : msg.replyTo.senderName}
                                                                </div>
                                                                <div className={`line-clamp-1 ${isMine ? "text-white/70" : "text-[#667781]"}`}>
                                                                    {msg.replyTo.text}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* File attachments — lazy loaded on click */}
                                                        {hasAnyFiles && (
                                                            <div className="mb-2 space-y-1.5">
                                                                {msgFiles.map((f: any) => (
                                                                    <LazyFileAttachment
                                                                        key={`${msg.id}-${f.id}`}
                                                                        pubnub={pubnub}
                                                                        channelId={msg.channelId}
                                                                        file={f}
                                                                        messageId={msg.id}
                                                                        isMine={isMine}
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}

                                                        <div className={msg.text ? "whitespace-pre-wrap break-words" : ""}>
                                                            {msg.text && (
                                                                <>
                                                                    <span>
                                                                        {shouldTruncate && !isExpanded ? msg.text.slice(0, 100) + "..." : msg.text}
                                                                    </span>
                                                                    {shouldTruncate && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleExpandMessage(String(msg.id));
                                                                            }}
                                                                            className={`ml-2 align-baseline text-[12px] font-medium focus:outline-none ${isMine ? "text-white/90" : "text-blue-600"}`}
                                                                        >
                                                                            {isExpanded ? "Read less" : "Read more"}
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                            <span className="inline-flex items-center gap-1 float-right ml-2 relative top-[2px]">
                                                                <span className={`text-[10px] ${isMine ? "text-white/70" : "text-[#667781]"}`}>
                                                                    {formatTime(msg.timestamp)}
                                                                </span>
                                                                {msg.isEdited && (
                                                                    <span className={`text-[10px] italic ${isMine ? "text-white/70" : "text-[#667781]"}`}>edited</span>
                                                                )}
                                                                {isMine && (
                                                                    <MessageTicks status={getMessageStatus(msg)} />
                                                                )}
                                                            </span>
                                                            <div className="clear-both" />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {msgReactions.length > 0 && !msg.isDeleted && (
                                                <div className={`absolute z-10 ${isMine ? "right-2" : "left-2"}`}>
                                                    <div className="flex items-center bg-white rounded-full px-1.5 py-0.5 shadow-md border border-gray-100">
                                                        {msgReactions.map((reaction: any, index: number) => (
                                                            <span
                                                                key={`${reaction.emoji}-${index}`}
                                                                className="text-[14px] cursor-pointer hover:scale-125 transition-transform"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (reaction.userId === currentUserId && !readOnly) {
                                                                        removeReaction(messageTimetoken, reaction.actionTimetoken);
                                                                    }
                                                                }}
                                                                title={reaction.userId === currentUserId ? "Click to remove" : ""}
                                                            >
                                                                {reaction.emoji}
                                                            </span>
                                                        ))}
                                                        {msgReactions.length > 1 && (
                                                            <span className="text-[10px] text-gray-500 ml-0.5 font-medium">
                                                                {msgReactions.length}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <ScrollToBottomButton
                    visible={showScrollToBottom}
                    onClick={scrollToBottom}
                />
            </div>

            {/* Input Area */}
            {readOnly ? (
                /* Read-only banner for Ongoing Chats tab */
                <div className="border-t bg-[#F0EAEE] px-4 py-3">
                    <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Lucide icon="Eye" className="w-4 h-4" />
                        <span className="text-sm">You are viewing this conversation in read-only mode.</span>
                    </div>
                </div>
            ) : (
                <div className="border-t bg-[#F7F1F5] p-3">
                    {editingMessage && (
                        <div className="mb-2 p-3 bg-white rounded-lg border-l-4 border-blue-500 flex items-start gap-2">
                            <Lucide icon="Pencil" className="w-4 h-4 text-blue-500 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-blue-600 mb-1">
                                    Editing message
                                </div>
                                <div className="text-sm text-slate-600 truncate">
                                    {editingMessage.text}
                                </div>
                            </div>
                            <button
                                onClick={cancelEdit}
                                className="text-slate-400 hover:text-red-500 p-1"
                            >
                                <Lucide icon="X" className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {replyingTo && !editingMessage && (
                        <div className="max-w-[300px] mb-2 p-3 bg-white rounded-lg border-l-4 border-purple-500 flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-purple-600 mb-1">
                                    Replying to {replyingTo.senderId === currentUserId ? "yourself" : replyingTo.senderName}
                                </div>
                                <div className="text-sm text-slate-600 truncate">
                                    {replyingTo.text}
                                </div>
                            </div>
                            <button
                                onClick={cancelReply}
                                className="text-slate-400 hover:text-red-500 p-1"
                            >
                                <Lucide icon="X" className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {selectedFiles.length > 0 && (
                        <div className="mb-2 p-2 bg-white rounded-lg">
                            <div className="flex items-center justify-between mb-1 px-1">
                                <span className="text-xs text-slate-500 font-medium">
                                    {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""} selected
                                    {selectedFiles.length < MAX_FILES_PER_MESSAGE && (
                                        <span className="text-slate-400"> · {MAX_FILES_PER_MESSAGE - selectedFiles.length} more allowed</span>
                                    )}
                                </span>
                                <button
                                    onClick={() => handleRemoveFile()}
                                    className="text-xs text-red-400 hover:text-red-600"
                                    disabled={isSending}
                                >
                                    Clear all
                                </button>
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:h-[4px]">
                                {selectedFiles.map((item) => (
                                    <div key={item.id} className="relative flex-shrink-0 w-16 h-16 group/prev">
                                        {item.preview && item.file.type.startsWith("image/") ? (
                                            <img
                                                src={item.preview}
                                                alt="Preview"
                                                className="w-16 h-16 object-cover rounded"
                                            />
                                        ) : /* VIDEO UPLOAD DISABLED: video preview thumbnail commented out */
                                        /* item.preview && item.file.type.startsWith("video/") ? (
                                            <video
                                                src={item.preview}
                                                className="w-16 h-16 object-cover rounded"
                                            />
                                        ) : */ (
                                            <div className="w-16 h-16 bg-purple-50 rounded flex flex-col items-center justify-center">
                                                <Lucide icon={getFileIcon(item.file.type) as any} className="w-5 h-5 text-purple-600" />
                                                <span className="text-[9px] text-slate-500 mt-0.5 truncate max-w-[56px] text-center">
                                                    {item.file.name.split(".").pop()?.toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                        <button
                                            onClick={() => handleRemoveFile(item.id)}
                                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/prev:opacity-100 transition-opacity"
                                            disabled={isSending}
                                        >
                                            <Lucide icon="X" className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <ChatMessageInput
                        value={inputMessage}
                        onChange={handleInputChange}
                        onSend={handleSendWithFile}
                        onEditSave={handleSaveEdit}
                        onCancelEdit={cancelEdit}
                        editing={!!editingMessage}
                        selectedFiles={selectedFiles.map((f) => f.file)}
                        onFileSelect={handleFileSelect}
                        onEmojiSelect={handleEmojiSelect}
                        allowedFileTypes={ALLOWED_FILE_TYPES}
                    />
                </div>
            )}

            <MessageActionMenu
                isOpen={actionMenuState.isOpen}
                position={actionMenuState.position}
                message={actionMenuState.message}
                handleStartEdit={handleStartEdit}
                handleShowDeleteModal={handleShowDeleteModal}
                closeActionMenu={() => setActionMenuState({ isOpen: false, message: null, position: { x: 0, y: 0 } })}
            />

            <ActionModal
                isOpen={deleteModal.isOpen}
                title="Delete message?"
                onClose={() => setDeleteModal({ isOpen: false, message: null })}
                actions={[
                    {
                        label: "Delete for me",
                        description: "Only you won't see this message",
                        icon: "User",
                        onClick: () => handleDelete(false),
                    },
                    {
                        label: "Delete for everyone",
                        description: "This message will be deleted for all",
                        icon: "Users",
                        variant: "danger",
                        onClick: () => handleDelete(true),
                    },
                ]}
            />

           { !readOnly && <MessageReactionPicker
                isOpen={reactionPickerState.isOpen}
                position={reactionPickerState.position}
                onReactionSelect={handleAddReaction}
                onClose={closeReactionPicker}
            /> }
           
        </div>
    );
};

export default OneToOneChatPanel;